<template>
    <div>
        <b-table :items="availableAcceptances" :fields="availableAcceptancesFields" show-empty
            empty-text="No acceptances available for patient" class="mb-0">
            <template #cell(actions)="{ item }">
                <div class="table-icon-actions">
                    <a v-if="!item.requiresSignature" href="#" @click.prevent="selectedConfig = item">
                        <b-icon icon="plus-circle" v-b-tooltip.hover title="Add Acceptance Record" />
                    </a>
                </div>
            </template>
        </b-table>

        <b-modal :visible="!!selectedConfig" title="Add Acceptance Record" ok-title="Add Acceptance Record"
            :ok-disabled="selectedSourceType === null || uploadingDocument" @hidden="resetModal" no-fade centered @ok="addAcceptance">
            <template v-if="selectedConfig">
                <p>Add an acceptance record for <strong>{{ selectedConfig.name }}</strong> for this patient?</p>

                <b-form-group label="Source Type">
                    <b-select v-model="selectedSourceType" :options="sourceTypeOptions" />
                </b-form-group>

                <b-form-group v-if="selectedSourceType === AcceptanceSource.Written" label="Upload Document (Optional)">
                    <b-form-file
                        v-model="documentFile"
                        accept=".pdf"
                        placeholder="Choose PDF document or drop it here..."
                        drop-placeholder="Drop file here...">
                    </b-form-file>
                </b-form-group>

                <div v-if="uploadingDocument">
                    <b-spinner small class="mr-2" />
                    <span>Uploading document...</span>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { AcceptanceConfiguration } from '@/models/AcceptanceConfiguration';
import { AcceptanceSource, getSourceTypeOptionsWithPlaceholder } from '@/models/AcceptanceSource';
import { Patient } from '@/models/Patient';
import { PatientDocument } from '@/models/PatientDocument';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import axios from 'axios';
import { BvModalEvent } from 'bootstrap-vue';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

@Component({
    components: {

    },
})
export default class AvailableAcceptanceListComponent extends Vue {

    @Prop({ required: true }) protected patient!: Patient;

    mounted() {
        this.loadAvailableAcceptances();
    }

    protected availableAcceptances: AcceptanceConfiguration[] = [];
    protected selectedConfig: AcceptanceConfiguration | null = null;
    protected selectedSourceType: AcceptanceSource | null = null;
    protected uploadingDocument = false;
    protected documentFile: File | null = null;
    protected AcceptanceSource = AcceptanceSource; // Make enum accessible in template

    get availableAcceptancesFields() {
        const boolFormatter = (value: boolean) => value ? "✔️" : "";
        return [
            { key: "name" },
            { key: "mandatory", formatter: boolFormatter },
            { key: "recurring", formatter: boolFormatter },
            { key: "requiresSignature", formatter: boolFormatter },
            { key: "actions", label: "", class: "actions-col" }
        ]
    }

    get sourceTypeOptions() {
        return getSourceTypeOptionsWithPlaceholder();
    }

    @Watch("patient")
    async loadAvailableAcceptances() {
        if (!this.patient?.id) {
            this.availableAcceptances = [];
            return;
        }
        try {
            const resp = await axios.get<AcceptanceConfiguration[]>(`Patient/AvailableAcceptanceConfigs/${this.patient.id}`);
            this.availableAcceptances = resp.data;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
            this.availableAcceptances = [];
        }
    }

    async addAcceptance(e: BvModalEvent) {
        e.preventDefault();

        if (this.selectedSourceType === null) {
            this.$notification(NotificationOptions.error("Please select a source type"));
            return;
        }

        try {
            let imageId: number | null = null;

            // Upload document first if provided
            if (this.documentFile && this.selectedSourceType === AcceptanceSource.Written) {
                this.uploadingDocument = true;
                const formData = new FormData();
                formData.append('document', this.documentFile);
                formData.append('fileName', `Acceptance - ${this.selectedConfig!.name}`);

                const uploadResp = await axios.post<PatientDocument>(
                    `/patient/${this.patient.id}/add-document/`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                imageId = uploadResp.data.imageId ?? null;
            }

            // Create acceptance record
            await axios.post('AcceptanceRecord/with-document', {
                configurationId: this.selectedConfig!.id,
                patientId: this.patient.id,
                imageId: imageId,
                sourceType: this.selectedSourceType,
                declined: false
            });

            this.$notification(NotificationOptions.success("Added acceptance"));
            this.loadAvailableAcceptances();
            this.resetModal();
            this.$emit("update");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.uploadingDocument = false;
        }
    }

    resetModal() {
        this.selectedConfig = null;
        this.documentFile = null;
        this.selectedSourceType = null;
        this.uploadingDocument = false;
    }

}

</script>

<style scoped>
::v-deep(.actions-col) {
    width: 30px;
}
</style>